import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Icon from "flarum/common/components/Icon";
import classList from "flarum/common/utils/classList";
import ChatDrawer from "./ChatDrawer";
import chatState from "../state/chat";
import { chatTitle, chatIcon } from "../utils/branding";
import { shouldUseChatDrawer } from "../utils/surface";

/**
 * The chat entry point in the header — Discourse's speech bubble next to search.
 *
 * The markup is a copy of core's `HeaderDropdown.getButtonContent()`, which is what
 * `flarum/messages` renders through `DialogsDropdown`: icon, then bubble, then
 * label, on a `.Button.Button--flat`. Matching it exactly is what makes the control
 * behave in both places it is drawn — as a round icon in the desktop header, and as
 * a named row inside the drawer on a phone — without a parallel set of rules.
 *
 * It is a plain `<button>` rather than a `HeaderDropdown` subclass because there is
 * no menu: the chat opens its own drawer or its page. Subclassing would drag in
 * `Dropdown-menu` markup and a toggle state that nothing here uses.
 */
export default class ChatNavButton extends Component {
  view() {
    const mentions = this.unreadMentions();
    const unread = this.unreadChannels();
    const icon = chatIcon();
    const label = chatTitle();

    // A count, the way core's notification bell shows one — not a dot. A dot says
    // "something happened" and leaves you to open the chat to find out how much,
    // which is the one question the header is there to answer.
    //
    // Mentions take precedence over ambient channel traffic, and the `new` class
    // below is what distinguishes them: core repaints `.HeaderDropdownBubble`
    // with the header colour under `.new`. That is the same signal the bell uses
    // for an unread notification, so the two read alike.
    const count = mentions > 0 ? mentions : unread;
    const digits = count > 99 ? "99+" : String(count);
    return m("button", {
      type: "button",
      className: classList("Button Button--flat ChatNavButton", {
        // Core styles `.new` on a header button to signal fresh activity.
        new: mentions > 0,
        hasIcon: icon !== null
      }),
      title: label,
      "aria-label": this.ariaLabel(mentions, unread > 0, label),
      onclick: () => this.open()
    }, icon ? m(Icon, {
      name: icon,
      className: "Button-icon"
    }) : null, count > 0 ? m("span", {
      className: "Bubble HeaderDropdownBubble",
      "data-digits": digits.length,
      "aria-hidden": "true"
    }, digits) : null, m("span", {
      className: "Button-label"
    }, m("span", {
      className: "Button-labelText"
    }, label)));
  }

  /**
   * Opens the drawer or the full-screen page, following the user's preference.
   *
   * Nothing closes Flarum's drawer on the way out: `ChatPage` extends core's
   * `Page`, whose `oninit` calls `app.drawer.hide()`. Doing it again here would
   * start the hide animation twice.
   */
  open() {
    if (shouldUseChatDrawer()) {
      ChatDrawer.open();
      return;
    }
    m.route.set(app.route("chat.index"));
  }

  /**
   * Read through ChatState rather than off the user record directly.
   *
   * The serialised attribute is a snapshot from page render; the summary prefers
   * the loaded channel list when there is one and falls back to that attribute
   * otherwise — and realtime keeps the attribute moving. Reading the attribute
   * here meant the dot only ever appeared after a reload, which is precisely when
   * it is least useful.
   */
  unreadChannels() {
    return chatState.unreadSummary().messages;
  }
  unreadMentions() {
    return chatState.unreadSummary().mentions;
  }

  /**
   * The bubble is aria-hidden, so this is the only thing a screen reader gets —
   * and it has to say which kind of unread it is, because the two carry different
   * urgency and the colour that distinguishes them is not announced.
   */
  ariaLabel(mentions, hasUnread, label) {
    if (mentions > 0) {
      return app.translator.trans("ramon-chat.forum.nav.unread_mentions", {
        count: mentions
      }, true);
    }
    if (hasUnread) {
      return app.translator.trans("ramon-chat.forum.nav.unread_messages", {
        count: this.unreadChannels()
      }, true);
    }
    return label;
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatNavButton', ChatNavButton);